import React from 'react';

import { Box, InputAdornment, Stack } from '@mui/material';

import styled from '@emotion/styled';
import { useField } from 'formik';

import { CurrencySign } from '../../../../../components/CurrencySign/CurrencySign';
import FormikNumberField from '../../../../../components/Formiks/NumberField/NumberField';
import FormikTextField from '../../../../../components/Formiks/TextField/TextField';
import { OfferCategory } from '../../../../../graphql/API';
import { useAppSelector } from '../../../../../redux/hooks';
import Icons from '../../../../../themes/icons';
import { Method } from '../../../ProductForm';
import SideBySide from '../../SideBySide';
import OfferPhoto from '../../formiks/OfferPhoto/OfferPhoto';
import { FormValue, StepOneFormValue } from '../../schema';

type Values = StepOneFormValue;

type Props = {
  method?: Method;
  initialValues?: Values;
  values: Values;
  handlePersistFormDataStep?: (values: Partial<FormValue>) => void;
  isShopifyProduct?: boolean;
};

export const Form1: React.FC<Props> = ({
  values,
  handlePersistFormDataStep,
  isShopifyProduct = false,
}) => {
  const venueState = useAppSelector((state) => state.venue.value);

  const [titleField, , titleFieldHelper] = useField('title');
  const [descriptionField, descriptionMeta, descriptionHelper] = useField('description');

  React.useEffect(() => {
    // const formattedValue = titleField.value.charAt(0).toUpperCase() + titleField.value.slice(1);
    // titleFieldHelper.setValue(formattedValue);
  }, [titleField.value, titleFieldHelper]);

  React.useEffect(() => {
    if (handlePersistFormDataStep) handlePersistFormDataStep({ ...(values as FormValue) });
  }, [values]);

  React.useEffect(() => {
    if ((descriptionField.value?.length || 0) > 500) {
      descriptionHelper.setTouched(true);
      descriptionHelper.setError('You have exceeded the 500-character limit');
    } else if (descriptionMeta.touched && descriptionMeta.error === 'You have exceeded the 500-character limit') {
      descriptionHelper.setError(undefined);
    }
  }, [descriptionField.value, descriptionHelper, descriptionMeta.touched, descriptionMeta.error]);

  return (
    <Box>
      <Stack spacing={6} sx={{ width: '100%' }}>
        <OfferPhoto isShopifyProduct={isShopifyProduct} />
        <CustomBox>
          <Label htmlFor='txt-title' className='offer-form-label'>
            Product title
          </Label>
          <FormikTextField
            id='txt-title'
            name='title'
            placeholder={
              values.category === OfferCategory.event
                ? 'Ex. Fashion week launch event'
                : 'Ex. dinner, haircut and brushing'
            }
            className='offer-input-field'
          />
        </CustomBox>
        <CustomBox>
          <LabelContainer>
            <Label htmlFor='txt-desc' className='offer-form-label'>
              Description
            </Label>
            <CharacterCounter isExceeded={(descriptionField.value?.length || 0) > 500}>
              {(descriptionField.value?.length || 0)}/500
            </CharacterCounter>
          </LabelContainer>
          <FormikTextField
            id='txt-desc'
            name='description'
            placeholder={
              values.category === OfferCategory.event
                ? 'Ex. Join us at the exclusive Fashion Week launch event for our brand. There will be a DJ, open bar, and lots of surprises!'
                : 'e.g  a rich, fast-absorbing body cream that deeply hydrates and nourishes the skin. '
            }
            multiline
            rows={5}
            className='offer-input-field'
            helperText=''
          />
          {(descriptionField.value?.length || 0) > 500 && (
            <ErrorContainer>
              <ErrorIconWrapper>
                <Icons.InfoLightIcon height={24} width={24} />
              </ErrorIconWrapper>
              <ErrorMessage>You have exceeded the 500-character limit</ErrorMessage>
            </ErrorContainer>
          )}
        </CustomBox>

        <SideBySide shouldFlexEnd>
          <CustomBox>
            <Label htmlFor='txt-offer-value' className='offer-form-label' style={{ paddingBottom: '0' }}>
              Product value
            </Label>
            <Subtitle>Estimated retail value of the product or service</Subtitle>
            <FormikNumberField
              id='txt-offer-value'
              name='offerValue'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <CurrencySign currency={venueState?.currency || 'usd'} />
                  </InputAdornment>
                ),
              }}
              className='offer-input-field'
            />
          </CustomBox>
        </SideBySide>
      </Stack>
    </Box>
  );
};

const CustomBox = styled.div`
  display: flex;
  flex-direction: column;
  fieldset {
    border: 1px solid #e6e6e6;
  }
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
`;

const Label = styled.label`
  color: #000;
  font-size: 15px;
  font-weight: 500;
`;

const CharacterCounter = styled.span<{ isExceeded: boolean }>`
  color: ${(props) => (props.isExceeded ? '#E05757' : '#B3B3B3')};
  font-size: 15px;
  font-weight: 400;
`;

const Subtitle = styled.div`
  font-family: 'Poppins', 'Helvetica', 'Arial', sans-serif;
  font-weight: 400;
  font-size: 13px;
  letter-spacing: 0%;
  color: rgba(17, 17, 17, 0.8);
  padding-bottom: 8px;
  white-space: nowrap;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  margin-top: 17px;
  color: #E05757;
`;

const ErrorIconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: #E05757;
  svg {
    fill: currentColor;
    stroke: none;
    stroke-width: 0;
    path {
      fill: currentColor;
      stroke: none;
      stroke-width: 0;
    }
  }
`;

const ErrorMessage = styled.span`
  color: #E05757;
  font-size: 15px;
  font-weight: 400;
  font-family: 'Poppins', 'Helvetica', 'Arial', sans-serif;
`;
